import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Typography, Grid, MenuItem } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { Formik, Form, Field, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { getCatalogItems, getAuctionDetail, updateAuction, checkAuctionCustomIdAvailiblity } from '../../apis';
import { AlertType, Auction } from '../../utilities/types';
import CustomAlert from '../elements/custom-alert.element';
import { getApiErrorMessage } from '../../utilities/helper';
import { showSnackbar } from '../../store/slices/common.slice';
import { priceRegex } from '../../constants';
import useUserHook from '../../hooks/user.hook';
import { addAuctionApi } from '../../apis/admin';

interface AuctionFormProps {
  auction_id?: number;
  context?: 'admin' | 'seller';
  onSuccess: () => void;
}

const initialValues = {
  seller_id: 0,
  title: '',
  description: '',
  item_id: 0,
  sub_item_id: 0,
  starting_price: 0,
  increment_price: 0,
  quantity: 0,
  quantity_unit: '',
  start_time: '',
  end_time: '',
  emd_amount: 0,
  security_deposit: 0,
  end_time_extension: 5,
  is_reversed_auction: false,
  is_admin_approved: false,
  material_lifting: '',
  custom_id: null,
};

// const validationSchema1 = Yup.object().test(
//     'price-validation',
//     'Current price must be greater than or equal to starting price',
//     (values) => {
//       if (!values.starting_price || !values.current_price) return true;
//       const start = parseFloat(values.starting_price);
//       const current = parseFloat(values.current_price);
//       return current >= start;
//     }
//   )
//   .test('end-after-start', 'End time must be after start time', (values) => {
//     if (!values.start_time || !values.end_time) return true;
//     const start = new Date(values.start_time);
//     const end = new Date(values.end_time);
//     return end > start;
//   });

const AuctionForm: React.FC<AuctionFormProps> = ({ onSuccess, auction_id, context = 'admin' }) => {
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<{
    seller_id: number;
    title: string;
    description: string;
    item_id: number;
    sub_item_id: number | null;
    starting_price: number;
    increment_price: number;
    quantity: number;
    quantity_unit: string;
    start_time: string;
    end_time: string;
    emd_amount: number;
    security_deposit: number;
    end_time_extension: number;
    is_reversed_auction: boolean;
    is_admin_approved?: boolean;
    material_lifting: string;
    custom_id: string | null;
  }> | null>(null);

  const { common, auth } = useSelector((state: any) => state);

  const { systemConfig } = common;
  const { user } = auth;

  const [items, setItems] = useState<any[]>([]);
  const [subItems, setSubItems] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [alertMessage, showAlertMessage] = useState<{ message: string; type: AlertType }>({
    message: '',
    type: 'info',
  });

  const [auctionData, setAuctionData] = useState<Auction | null>(null);

  const toggleAlertMessage = (message: string, type: AlertType) => {
    if (message === '') {
      return;
    }
    showAlertMessage({ message, type });
    setTimeout(() => {
      showAlertMessage({ message: '', type: 'info' });
    }, 4000);
  };

  const getItems = useCallback(async () => {
    getCatalogItems({})
      .then((response: any) => {
        if (response.status) {
          setItems(response.data.items);
        }
      })
      .catch(() => {
        dispatch(
          showSnackbar({ message: 'Something went wrong while fetching items', severity: 'error' })
        );
      });
  }, [dispatch]);

  const onsubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!formikRef.current) {
      return;
    }
    const values = { ...formikRef.current.values };
    const payload = { ...auctionData, ...values};
    if (context !== 'admin') {
      payload.seller_id = user.id;
    }
    setIsSubmitting(true);
    try {
      try {
        const checkPayload = {custom_id: values?.custom_id, auction_id: auction_id ?? null};
        const response = await checkAuctionCustomIdAvailiblity(checkPayload);
        if (!response.status) {
          throw new Error('Fail to check custom id availability');
        }
        if (!response.data) {
          throw new Error('Custom Id is not available. Please try with other value');
        }
      } catch (err: any) {
        throw new Error(err.message);
      }
      let response = null;
      if (auction_id) {
        response = await updateAuction(payload);
      } else {
        response = await addAuctionApi(payload);
      }
      if (response.status) {
        onSuccess();
      }
    } catch (err: any) {
      toggleAlertMessage(getApiErrorMessage(err), 'error');
    }
    setIsSubmitting(false);
  };

  const prepareSubItemsList = (itemId: number) => {
    const item = items.find((row) => row.id === itemId);
    if (item && item.sub_items.length > 0) {
      setSubItems(item.sub_items);
    } else {
      setSubItems([]);
    }
  };

  const getDetail = useCallback(async () => {
    try {
      const response: any = await getAuctionDetail({ auction_id });
      if (response.status) {
        setAuctionData({ ...response.data });
      }
    } catch (err) {
      dispatch(
        showSnackbar({
          message: 'Something went wrong while fetching auction detail',
          severity: 'error',
        })
      );
    }
  }, [dispatch, auction_id]);

  useEffect(() => {
    if (!auction_id) {
      return;
    }
    getDetail();
  }, [auction_id, getDetail]);

  useEffect(() => {
    getItems();
  }, [getItems]);

  useEffect(() => {
    if (formikRef.current && Object.keys(auctionData ?? {}).length) {
      setTimeout(() => formikRef.current?.validateForm(), 0);
    }
  }, [auctionData]);

  const { users } = useUserHook('seller');

  const schema: any = {
    title: Yup.string()
      .min(3, 'Title required minimum 10 characters')
      .required('Title is required'),

    description: Yup.string()
      .min(10, 'Description required minimum 10 characters')
      .max(200, 'Description should have maximum 200 characters')
      .required('Description is required'),

    item_id: Yup.number().min(1, 'Please select a valid item').required('Item ID is required'),

    quantity: Yup.number()
      .positive('Quantity must be a positive number')
      .required('Quantity is required'),

    quantity_unit: Yup.string().required('Quantity unit is required'),

    starting_price: Yup.string()
      .required('Starting price is required')
      .matches(
        priceRegex,
        'Starting price must have up to 10 digits before decimal and up to 2 digits after decimal'
      ),

    increment_price: Yup.string()
      .required('Incremen price is required')
      .matches(
        priceRegex,
        'Increment price must have up to 10 digits before decimal and up to 2 digits after decimal'
      ),

    start_time: Yup.date()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .typeError('Start time must be a valid date')
      .required('Start time is required'),

    end_time: Yup.date()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .typeError('End time must be a valid date')
      .required('End time is required')
      .test('not-in-past', 'End time cannot be in the past', (value: Date) => {
        if (!value) return false;
        return value >= new Date();
      }),

    emd_amount: Yup.string()
      .required('EMD amount is required')
      .matches(
        priceRegex,
        'EMD amount must have up to 10 digits before decimal and up to 2 digits after decimal'
      ),

    security_deposit: Yup.string()
      .required('Security deposit is required')
      .matches(
        priceRegex,
        'Security deposit must have up to 10 digits before decimal and up to 2 digits after decimal'
      ),

    end_time_extension: Yup.number()
      .positive('Time extension must be a positive number')
      .required('Time extension is required'),

    is_reversed_auction: Yup.boolean()
      .required('Reverse auction is required')
      .oneOf([true, false], 'Invalid Reverse auction selection')

    // custom_id: Yup.string()
    //   .required('Custom id is required')
    //   .min(3, 'Minimum 3 characters'),
  };

  if (subItems.length > 0) {
    schema.sub_item_id = Yup.number()
      .integer('Sub Item ID must be an integer')
      .nullable()
      .notRequired();
  }

  if (!auction_id && context === 'admin') {
    schema.seller_id = Yup.number().positive('Seller is invalid').required('Seller is required');
  }

  const validationSchema = Yup.object().shape(schema);

  // console.log('=============auctionData', auctionData);

  return (
    <Box>
      <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
        {!auction_id ? `Add New Auction` : `Edit Auction`}
      </Typography>

      {/* Formik Form */}
      <Formik
        innerRef={formikRef}
        validateOnMount
        enableReinitialize
        initialValues={{ ...initialValues, ...auctionData }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          console.log('Form Submitted:', { ...values });
        }}
      >
        {({ errors, touched, handleChange, values, isValid, dirty, setFieldValue }) => {
          // console.log('========errors', errors);
          // console.log('========check', !isValid, !auction_id, !dirty, isSubmitting);
          // console.log('========disable', !isValid || (!auction_id && !dirty) || isSubmitting);
          return <Form>
            {!auction_id && context === 'admin' && (
              <Field
                as={TextField}
                select
                name="seller_id"
                label="Select Seller *"
                fullWidth
                margin="normal"
                value={values.seller_id}
                onChange={handleChange}
                error={touched.seller_id && Boolean(errors.seller_id)}
                helperText={touched.seller_id && errors.seller_id}
              >
                <MenuItem value="">Select Seller</MenuItem>
                {users.map((item: any, index: number) => (
                  <MenuItem key={`user-option-${index}`} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Field>
            )}
            <Grid container spacing={2}>
              <Grid size={{xs: 12, sm: 6}}>
                <Field
                  as={TextField}
                  name="title"
                  label="Title *"
                  fullWidth
                  margin="normal"
                  value={values.title}
                  onChange={handleChange}
                  error={touched.title && Boolean(errors.title)}
                  helperText={touched.title && errors.title}
                />
              </Grid>
              <Grid size={{xs: 12, sm: 6}}>
                <Field
                  as={TextField}
                  name="custom_id"
                  label="Custom Id"
                  fullWidth
                  margin="normal"
                  value={values.custom_id}
                  onChange={handleChange}
                  error={touched.custom_id && Boolean(errors.custom_id)}
                  helperText={touched.custom_id && errors.custom_id}
                />
              </Grid>
            </Grid>
            <Field
              as={TextField}
              name="description"
              label="Description *"
              fullWidth
              margin="normal"
              multiline
              rows={4}
              value={values.description}
              onChange={handleChange}
              error={touched.description && Boolean(errors.description)}
              helperText={touched.description && errors.description}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: subItems.length > 0 ? 6 : 12 }}>
                <Field
                  as={TextField}
                  select
                  name="item_id"
                  label="Select Item *"
                  fullWidth
                  margin="normal"
                  value={values.item_id}
                  onChange={(e: any) => {
                    setFieldValue('item_id', e.target.value);
                    prepareSubItemsList(e.target.value);
                  }}
                  error={touched.item_id && Boolean(errors.item_id)}
                  helperText={touched.item_id && errors.item_id}
                >
                  <MenuItem value="0">Select Item</MenuItem>
                  {items.map((item: any, index: number) => (
                    <MenuItem key={`item-option-${index}`} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
              {subItems.length > 0 && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    as={TextField}
                    select
                    name="sub_item_id"
                    label="Select SubItem *"
                    fullWidth
                    margin="normal"
                    value={values.sub_item_id}
                    onChange={(e: any) => setFieldValue('sub_item_id', e.target.value)}
                    error={touched.sub_item_id && Boolean(errors.sub_item_id)}
                    helperText={touched.sub_item_id && errors.sub_item_id}
                  >
                    <MenuItem value="0">Select Sub Item</MenuItem>
                    {subItems.map((item: any, index: number) => (
                      <MenuItem key={`item-option-${index}`} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
              )}
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  name="starting_price"
                  label="Start Price *"
                  type="number"
                  fullWidth
                  margin="normal"
                  placeholder="Enter starting price"
                  inputProps={{ step: '0.01', min: '0' }}
                  value={values.starting_price}
                  onChange={handleChange}
                  error={touched.starting_price && Boolean(errors.starting_price)}
                  helperText={touched.starting_price && errors.starting_price}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  name="increment_price"
                  label="Bid Increment Price *"
                  type="number"
                  fullWidth
                  margin="normal"
                  placeholder="Enter bid increment price"
                  inputProps={{ step: '0.01', min: '0' }}
                  value={values.increment_price}
                  onChange={handleChange}
                  error={touched.increment_price && Boolean(errors.increment_price)}
                  helperText={touched.increment_price && errors.increment_price}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  name="quantity"
                  label="Quantity *"
                  type="number"
                  fullWidth
                  margin="normal"
                  placeholder="Enter Quantity"
                  inputProps={{ step: '1', min: '0' }}
                  value={values.quantity}
                  onChange={handleChange}
                  error={touched.quantity && Boolean(errors.quantity)}
                  helperText={touched.quantity && errors.quantity}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  select
                  name="quantity_unit"
                  label="Select Quantity Unit *"
                  fullWidth
                  margin="normal"
                  value={values.quantity_unit}
                  onChange={(e: any) => setFieldValue('quantity_unit', e.target.value)}
                  error={touched.quantity_unit && Boolean(errors.quantity_unit)}
                  helperText={touched.quantity_unit && errors.quantity_unit}
                >
                  <MenuItem value="">Select Quantity Unit</MenuItem>
                  {Object.entries(systemConfig.qty_units ?? {}).map(([key, value]) => (
                    <MenuItem key={`item-option-${key}`} value={key}>
                      {value != null ? String(value) : ''}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DateTimePicker
                  label="Start Date *"
                  value={values.start_time ? dayjs(values.start_time) : null}
                  onChange={(val) => setFieldValue('start_time', val)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'normal',
                      error: touched.start_time && Boolean(errors.start_time),
                      helperText: touched.start_time && errors.start_time,
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DateTimePicker
                  label="End Date *"
                  value={values.end_time ? dayjs(values.end_time) : null}
                  onChange={(val) => setFieldValue('end_time', val)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'normal',
                      error: touched.end_time && Boolean(errors.end_time),
                      helperText: touched.end_time && errors.end_time,
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  name="emd_amount"
                  label="EMD Amount *"
                  type="number"
                  fullWidth
                  margin="normal"
                  placeholder="Enter EMD Amount"
                  inputProps={{ step: '0.01', min: '0' }}
                  value={values.emd_amount}
                  onChange={handleChange}
                  error={touched.emd_amount && Boolean(errors.emd_amount)}
                  helperText={touched.emd_amount && errors.emd_amount}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  name="security_deposit"
                  label="Security Deposit *"
                  type="number"
                  fullWidth
                  margin="normal"
                  placeholder="Enter Security Deposit"
                  inputProps={{ step: '0.01', min: '0' }}
                  value={values.security_deposit}
                  onChange={handleChange}
                  error={touched.security_deposit && Boolean(errors.security_deposit)}
                  helperText={touched.security_deposit && errors.security_deposit}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  name="end_time_extension"
                  label="End Time Extension (in Mins) *"
                  type="number"
                  fullWidth
                  margin="normal"
                  placeholder="Enter End Time Extension (in Mins)"
                  inputProps={{ step: '0.01', min: '0' }}
                  value={values.end_time_extension}
                  onChange={handleChange}
                  error={touched.end_time_extension && Boolean(errors.end_time_extension)}
                  helperText={touched.end_time_extension && errors.end_time_extension}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field
                  as={TextField}
                  select
                  name="is_reversed_auction"
                  label="Is Reversed Auction *"
                  fullWidth
                  margin="normal"
                  value={values.is_reversed_auction}
                  onChange={(e: any) => setFieldValue('is_reversed_auction', e.target.value)}
                  error={touched.is_reversed_auction && Boolean(errors.is_reversed_auction)}
                  helperText={touched.is_reversed_auction && errors.is_reversed_auction}
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </Field>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: context === 'admin' ? 6 : 12 }}>
                <Field
                  as={TextField}
                  name="material_lifting"
                  label="Material Lifting *"
                  fullWidth
                  margin="normal"
                  value={values.material_lifting}
                  onChange={handleChange}
                  error={touched.material_lifting && Boolean(errors.material_lifting)}
                  helperText={touched.material_lifting && errors.material_lifting}
                />
              </Grid>
              {context === 'admin' && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    as={TextField}
                    select
                    name="is_admin_approved"
                    label="Is Admin Approved"
                    fullWidth
                    margin="normal"
                    value={values.is_admin_approved}
                    onChange={(e: any) => setFieldValue('is_admin_approved', e.target.value === 'true' ? true : false )}
                    error={touched.is_admin_approved && Boolean(errors.is_admin_approved)}
                    helperText={touched.is_admin_approved && errors.is_admin_approved}
                  >
                    <MenuItem value="false">No</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                  </Field>
                </Grid>
              )}
            </Grid>

            {/* Submit Button */}
            <Button
              type="button"
              variant="contained"
              fullWidth
              sx={{ mt: 2, bgcolor: 'green', '&:hover': { bgcolor: 'darkgreen' } }}
              onClick={(e) => onsubmit(e)}
              disabled={!isValid || (!auction_id && !dirty) || isSubmitting}
            >
              {auction_id ? 'Update Auction' : 'Create Auction'}
            </Button>
            {alertMessage.message !== '' && (
              <Box sx={{ my: 1 }}>
                <CustomAlert type={alertMessage.type}>{alertMessage.message}</CustomAlert>
              </Box>
            )}
          </Form>
        }}
      </Formik>
    </Box>
  );
};

export default AuctionForm;
